import { ACTIVE_STATUS_ACCOUNTS } from 'constants/globals';
import { ascSort, sortArrayOfAlphaNum, arrayValidation } from 'utils/array';

import type { Client } from 'api/client/models';
import type { ChipDataType } from 'store/types/clients-store';
import type LabelValue from 'commons/modules/label-value';

import { KEY_FILTER_OPTIONS } from './constants';

const getUniques = (options: LabelValue[]) => {
  if (!arrayValidation(options)) return [];

  const newOptions: LabelValue[] = [];

  for (let index = 0; index < options.length; index++) {
    const element = options[index];
    const option = newOptions.find((item) => item.value === element.value);
    if (!option) {
      newOptions.push(element);
    }
  }
  return newOptions;
};

export const getCatalogsByData = (data: Client[]) => {
  const clients = data.map((item) => ({
    label: item.fullName || '',
    value: item.fullName,
  }));
  const legalRepresentative = getUniques(
    data
      .filter((item) => item.legalRepresentativeDto)
      .map((item) => ({
        label: String(item.legalRepresentativeDto?.fullName).toUpperCase(),
        value: String(item.legalRepresentativeDto?.fullName).toUpperCase(),
      })),
  );
  const cveInternal = data.map((item) => ({
    label: item.cveInternal || '',
    value: item.cveInternal,
  }));
  const accounts: LabelValue[] = [];
  data.forEach((item) => {
    if (item && arrayValidation(item.accounts)) {
      item.accounts.forEach((account) => {
        accounts.push({
          label: account.accountNumber || '',
          value: account.accountNumber,
        });
      });
    }
  });

  return {
    clients: ascSort(clients, 'label'),
    legalRepresentative: ascSort(legalRepresentative, 'label'),
    cveInternal: sortArrayOfAlphaNum(cveInternal, 'label'),
    accounts: sortArrayOfAlphaNum(accounts, 'label'),
  };
};

const getDataByClient = (chipItem: ChipDataType, newData: Client[]) => {
  return newData.filter((client) => client.fullName === chipItem.value);
};

const getDataByBrokerageHouse = (chipItem: ChipDataType, newData: Client[]) => {
  return newData.filter((client) => {
    if (arrayValidation(client.accounts)) {
      const accounts = client.accounts.filter(
        (account) => account.cveStatus === ACTIVE_STATUS_ACCOUNTS,
      );

      if (arrayValidation(accounts)) {
        const banks = accounts.filter(
          (account) =>
            String(account.idBrokerageHouse).toUpperCase() === chipItem.value,
        );
        return arrayValidation(banks);
      }
    }
    return false;
  });
};

const getDataByStatus = (chipItem: ChipDataType, newData: Client[]) => {
  return newData.filter((client) => client.cveStatus === chipItem.value);
};

const getDataByProfile = (chipItem: ChipDataType, newData: Client[]) => {
  return newData.filter(
    (client) =>
      client.profile === chipItem.value ||
      client.profileAuth === chipItem.value,
  );
};

const getDataByLegalRepresentative = (
  chipItem: ChipDataType,
  newData: Client[],
) => {
  return newData.filter(
    (client) =>
      client.legalRepresentativeDto &&
      String(client.legalRepresentativeDto.fullName).toUpperCase() ===
        chipItem.value,
  );
};

const getDataByProfileCveInternal = (
  chipItem: ChipDataType,
  newData: Client[],
) => {
  return newData.filter((client) => client.cveInternal === chipItem.value);
};

const getDataByAccount = (chipItem: ChipDataType, newData: Client[]) => {
  return newData.filter((client) => {
    if (arrayValidation(client.accounts)) {
      const accounts = client.accounts.filter(
        (account) => account.cveStatus === ACTIVE_STATUS_ACCOUNTS,
      );

      if (arrayValidation(accounts)) {
        const banks = accounts.filter(
          (account) =>
            String(account.accountNumber || '').toUpperCase() ===
            String(chipItem.value).toUpperCase(),
        );
        return arrayValidation(banks);
      }
    }
    return false;
  });
};

export const getDataByFilters = (
  chipData: ChipDataType[],
  dataOriginal: Client[],
) => {
  let newData = [...dataOriginal];

  chipData.forEach((chipItem) => {
    if (arrayValidation(newData)) {
      switch (chipItem.filterBy) {
        case KEY_FILTER_OPTIONS.client:
          newData = getDataByClient(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.bank:
          newData = getDataByBrokerageHouse(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.status:
          newData = getDataByStatus(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.profile:
          newData = getDataByProfile(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.legalRepresentative:
          newData = getDataByLegalRepresentative(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.cveInternal:
          newData = getDataByProfileCveInternal(chipItem, newData);
          break;
        case KEY_FILTER_OPTIONS.account:
          newData = getDataByAccount(chipItem, newData);
          break;
        default:
          break;
      }
    }
  });
  return newData;
};
